import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Trophy, Rocket, Flag, Medal, Flame, Star, Lock } from 'lucide-react';
import useAuthStore from '../stores/authStore';
import { getLeaderboard, getStudentBadges } from '../lib/api';

const SCOPES = [
  { value: 'college', label: 'College' },
  { value: 'batch', label: 'Batch' },
  { value: 'branch', label: 'Branch' },
  { value: 'global', label: 'Global' },
];

const BADGE_ICONS = { Rocket, Flag, Medal, Flame, Star, Trophy };

const RANK_STYLES = {
  1: 'bg-[#FFC233]/20 text-[#C68F00] dark:text-[#FFC233]',
  2: 'bg-[#9BA1B0]/20 text-[#6B6480] dark:text-[#C3C2B7]',
  3: 'bg-[#FF6B1A]/20 text-[#FF6B1A]',
};

export default function LeaderboardPage() {
  const { user } = useAuthStore();
  const [scope, setScope] = useState('college');
  const [board, setBoard] = useState(null);
  const [badges, setBadges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadBoard = useCallback(async (nextScope) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getLeaderboard(nextScope);
      setBoard(data);
    } catch (e) {
      setError('Could not load the leaderboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBoard(scope); }, [scope, loadBoard]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getStudentBadges();
        if (!cancelled) setBadges(data);
      } catch {
        // non-critical — leaderboard still renders without badges
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-heading uppercase tracking-[0.2em] text-[#3D1F6E] dark:text-[#A78BFA] font-semibold mb-1">
          Gamification
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-heading">
          Leaderboard
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          See how you rank against your peers
        </p>
      </div>

      <Tabs value={scope} onValueChange={setScope}>
        <TabsList data-testid="leaderboard-scope-tabs">
          {SCOPES.map((s) => (
            <TabsTrigger key={s.value} value={s.value} data-testid={`leaderboard-scope-${s.value}`}>
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {error && <p className="text-sm text-red-500" data-testid="leaderboard-error">{error}</p>}

      <Card className="bg-card border-border card-shadow">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
              Rankings
            </CardTitle>
            {board?.my_rank && (
              <span className="text-[11px] text-muted-foreground" data-testid="leaderboard-my-rank">
                Your rank: <span className="font-semibold text-foreground">#{board.my_rank}</span>
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {!loading && (!board?.entries || board.entries.length === 0) ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No ranked students in this scope yet. Complete an interview to appear here.
            </p>
          ) : (
            board?.entries?.map((entry) => (
              <LeaderboardRow key={entry.student_id} entry={entry} isMe={entry.student_id === user?.user_id} />
            ))
          )}
        </CardContent>
      </Card>

      {badges && (
        <Card className="bg-card border-border card-shadow">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-heading font-semibold text-[#3D1F6E] dark:text-[#A78BFA]">
                Badges
              </CardTitle>
              <span className="text-[11px] text-muted-foreground">
                {badges.earned_count} / {badges.total_count} earned
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="badges-grid">
              {badges.badges.map((b) => (
                <BadgeTile key={b.id} badge={b} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function LeaderboardRow({ entry, isMe }) {
  const rankStyle = RANK_STYLES[entry.rank] || 'bg-secondary text-muted-foreground';
  return (
    <div
      data-testid={`leaderboard-row-${entry.rank}`}
      className={`flex items-center gap-3 p-3 rounded-xl border transition-colors
        ${isMe ? 'border-[#3D1F6E] dark:border-[#A78BFA] bg-[#3D1F6E]/5 dark:bg-[#A78BFA]/10' : 'border-border'}`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 font-heading font-bold text-xs ${rankStyle}`}>
        {entry.rank}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold truncate">
          {entry.student_name || 'Student'} {isMe && <span className="text-[10px] text-muted-foreground font-normal">(You)</span>}
        </p>
        <p className="text-[11px] text-muted-foreground truncate">
          {entry.college_name} {entry.batch && `• Batch ${entry.batch}`} {entry.branch && `• ${entry.branch}`}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-heading font-bold tabular-nums">{Math.round(entry.avg_score)}</p>
        <p className="text-[10px] text-muted-foreground">{entry.total_sessions} sessions</p>
      </div>
    </div>
  );
}

function BadgeTile({ badge }) {
  const Icon = BADGE_ICONS[badge.icon] || Trophy;
  return (
    <div
      data-testid={`badge-${badge.id}`}
      className={`flex flex-col items-center text-center gap-2 p-3 rounded-xl border ${
        badge.earned ? 'border-[#FFC233]/40 bg-[#FFC233]/5' : 'border-border opacity-50'
      }`}
      title={badge.description}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
        badge.earned ? 'bg-[#FFC233]/20 text-[#C68F00] dark:text-[#FFC233]' : 'bg-secondary text-muted-foreground'
      }`}>
        {badge.earned ? <Icon className="w-5 h-5" /> : <Lock className="w-4 h-4" />}
      </div>
      <p className="text-[11px] font-semibold font-heading leading-tight">{badge.label}</p>
    </div>
  );
}
